import {
  controlPlaneHistoryEstimateSchema,
  controlPlaneMultiConnectionSummarySchema,
  isControlPlaneCoverageReconciled,
} from "@obsina/domain";
import type {
  ControlPlaneConnection,
  ControlPlaneCoverage,
  ControlPlaneCoverageMetric,
  ControlPlaneIssue,
  ControlPlaneHistoryEstimate,
  ControlPlaneMultiConnectionSummary as DomainControlPlaneSummary,
  ControlPlaneProcessingFlow as DomainControlPlaneProcessingFlow,
  ControlPlaneProcessingStage as DomainControlPlaneProcessingStage,
  ControlPlaneProcessingStageKind as DomainControlPlaneProcessingStageKind,
  ControlPlaneSourceKind as DomainControlPlaneSourceKind,
  ControlPlaneSourceProcessing as DomainControlPlaneSourceProcessing,
  ControlPlaneSourceType as DomainControlPlaneSourceType,
  ControlPlaneState as DomainControlPlaneState,
  VisibilityClass as DomainVisibilityClass,
} from "@obsina/domain";

export type ControlPlaneState = DomainControlPlaneState;
export type VisibilityClass = DomainVisibilityClass;
export type CoverageMetric = ControlPlaneCoverageMetric;
export type Coverage = ControlPlaneCoverage;
export type ConnectionSummary = ControlPlaneConnection;
export type SourceKind = DomainControlPlaneSourceKind;
export type SourceType = DomainControlPlaneSourceType;
export type SourceProcessing = DomainControlPlaneSourceProcessing;
export type ProcessingFlow = DomainControlPlaneProcessingFlow;
export type ProcessingStage = DomainControlPlaneProcessingStage;
export type ProcessingStageKind = DomainControlPlaneProcessingStageKind;
export type { ControlPlaneIssue };
export type { ControlPlaneHistoryEstimate };

/**
 * The web control plane consumes contract 3: a `connections` array rather than
 * a single nullable connection. The top-level `state`, `coverage`,
 * `syncProgress` and `issues` remain the aggregate view. Today the tenant holds
 * at most one Microsoft mailbox connection, so `connections` has 0 or 1 entry;
 * the grid is authored for many, but only real connections are ever drawn.
 */
export type ControlPlaneSummary = DomainControlPlaneSummary & {
  /** Development-only marker; strict API payload parsing never accepts it. */
  synthetic?: boolean;
};

export function parseControlPlaneSummary(value: unknown): ControlPlaneSummary {
  return controlPlaneMultiConnectionSummarySchema.parse(value);
}

/**
 * The Microsoft mailbox connection the per-connection detail surfaces (history,
 * coverage, scope, sync progress) manage. Selected by provider rather than
 * position so a second connection (e.g. WhatsApp) in the array can never be
 * mistaken for the primary; null when no Microsoft connection is present.
 */
export function primaryConnection(
  summary: ControlPlaneSummary,
): ConnectionSummary | null {
  return (
    summary.connections.find(
      (connection) => connection.provider === "microsoft",
    ) ?? null
  );
}

/** The member's WhatsApp connections, drawn as their own grid cards. */
export function whatsappConnections(
  summary: ControlPlaneSummary,
): readonly ConnectionSummary[] {
  return summary.connections.filter(
    (connection) => connection.provider === "whatsapp",
  );
}

/**
 * What a WhatsApp connection actually is: a device that is linked, or a
 * recorded consent still waiting for one. A connection row exists from the
 * moment consent is recorded — the device link happens afterwards, and can
 * never happen at all — so "a connection is present" is not the same claim as
 * "WhatsApp is connected".
 */
export type WhatsAppLinkState = "linked" | "awaiting_device";

/**
 * Reads that state from the contract 3 payload.
 *
 * The payload carries no connection status, but it does carry the field that
 * answers this honestly: the API resolves `visibilityClass` / `ownerMemberId`
 * only for a connection that has left `pending`, and a WhatsApp connection
 * leaves `pending` exactly when the bridge reports a linked device. Its row is
 * inserted `owner_private` at consent, so nulls arriving here cannot mean an
 * undecided scope — they mean no device linked, therefore no message read.
 *
 * Unknown resolves to `awaiting_device`, never to `linked`. Being wrong that
 * way offers a completion path that reruns an idempotent consent + pairing;
 * being wrong the other way is what locked the member out — a card claiming a
 * connection they do not have, with no way to finish the one they started.
 */
export function whatsappLinkState(
  connection: ConnectionSummary,
): WhatsAppLinkState {
  return connection.visibilityClass === null ||
    connection.ownerMemberId === null
    ? "awaiting_device"
    : "linked";
}

export function parseHistoryEstimate(
  value: unknown,
): ControlPlaneHistoryEstimate {
  return controlPlaneHistoryEstimateSchema.parse(value);
}

export function isCoverageReconciled(coverage: Coverage): boolean {
  return isControlPlaneCoverageReconciled(coverage);
}
